// Cálculos puros del módulo de Licitaciones primarias.
//
// Reglas de negocio:
// - Tramo No Competitivo adjudica siempre (no lleva tasa/precio ofertado).
// - Modalidad 'Tasa' (subasta holandesa de tasa): adjudican las ofertas con
//   tasa ofertada <= tasa de corte. Todos pagan el precio de corte; si la
//   especie no informa precioCorte se asume emisión a la par (monto = VN).
// - Modalidad 'Precio': adjudican las ofertas con precio ofertado >= precio
//   de corte. El monto a liquidar es VN x precioCorte / 1000 (precio c/1000).
// - Los montos propuestos son un punto de partida: el usuario los ajusta por
//   prorrateos y redondeos del colocador antes de confirmar.
// - La carga de órdenes se cierra sola el día siguiente a la licitación: lo
//   ofertado no se toca más una vez que la subasta ocurrió.
//
// INVARIANTE DE MONEDAS: el VN, el precio y por lo tanto `montoAdjudicado` están
// siempre en la MONEDA DE EMISIÓN de la especie. Las especies dólar linked y
// duales se emiten en USD pero se integran en pesos, así que el monto que el
// cliente tiene que fondear sale de multiplicar por el tipo de cambio de la
// especie (Com. A 3500 del día T-1). Esa conversión se hace acá, en la capa de
// caja/KPIs, y nunca se persiste: si el TC se corrige, todo se recalcula.

import { MONEDA_MOVIMIENTO, bucketDeMoneda } from '../constants/monedas';
import { bpsONull, resolverColocadorOrden } from './colocadores';

const isOrdenActiva = (orden) => orden.estado !== 'Eliminada';

/**
 * Estado derivado de bloqueo de una licitación.
 * La carga se cierra por estado (≠ Abierta) o porque la fecha de licitación ya
 * pasó; en ese segundo caso una reapertura manual vigente la vuelve a habilitar.
 *
 * @param {object} licitacion
 * @param {string} hoyIso - fecha de hoy en formato 'YYYY-MM-DD' (toIsoDate)
 */
export const getLicitacionLock = (licitacion, hoyIso) => {
  const vencidaPorFecha = !!licitacion?.fechaLicitacion && hoyIso > licitacion.fechaLicitacion;
  const reaperturaVigente = !!licitacion?.cargaReabiertaHasta && licitacion.cargaReabiertaHasta >= hoyIso;
  const cerradaPorEstado = licitacion?.estado !== 'Abierta';
  const cargaCerrada = cerradaPorEstado || (vencidaPorFecha && !reaperturaVigente);

  let motivo = '';
  if (cerradaPorEstado) {
    motivo = licitacion?.estado ? `La licitación está ${licitacion.estado}` : 'La licitación no está abierta';
  } else if (cargaCerrada) {
    motivo = 'La fecha de licitación ya pasó';
  }

  return { vencidaPorFecha, reaperturaVigente, cargaCerrada, motivo };
};

export const adjudicaOrden = (orden, especie) => {
  if (orden.tramo === 'No Competitivo') return true;
  if (especie.modalidad === 'Tasa') {
    return (parseFloat(orden.tasaOfertada) || 0) <= (parseFloat(especie.tasaCorte) || 0);
  }
  return (parseFloat(orden.precioOfertado) || 0) >= (parseFloat(especie.precioCorte) || 0);
};

export const montoPropuesto = (vnAdjudicado, especie) => {
  const vn = parseFloat(vnAdjudicado) || 0;
  const precioCorte = parseFloat(especie.precioCorte) || 0;
  if (especie.modalidad === 'Precio') {
    return vn * precioCorte / 1000;
  }
  // Modalidad Tasa: a la par salvo que se cargue un precio de corte c/1000.
  return precioCorte > 0 ? vn * precioCorte / 1000 : vn;
};

/**
 * Monto sugerido para un VN adjudicado, tolerando que la especie todavía no
 * tenga corte cargado (carga manual de lo que informó el colocador).
 * Prioridad: precio de corte de la especie → precio ofertado de la propia orden
 * (modalidad Precio) → a la par (monto = VN).
 */
export const montoAdjudicadoSugerido = (orden, especie, vnAdjudicado) => {
  const vn = parseFloat(vnAdjudicado) || 0;
  const precioCorte = parseFloat(especie?.precioCorte) || 0;
  if (precioCorte > 0) return montoPropuesto(vn, { ...especie, precioCorte });
  if (especie?.modalidad === 'Precio') {
    const precioOfertado = parseFloat(orden?.precioOfertado) || 0;
    if (precioOfertado > 0) return vn * precioOfertado / 1000;
  }
  return vn;
};

/**
 * Reparte un VN total adjudicado proporcionalmente al VN solicitado de cada
 * fila incluida. La última fila incluida absorbe la diferencia de redondeo para
 * que el total cuadre exacto. Ninguna fila adjudica más de lo que pidió: si el
 * total a repartir supera lo solicitado, el excedente queda sin asignar (la UI
 * lo marca como descuadre).
 *
 * @param {Array} filas - [{ ordenId, cantidadVN, incluida }]
 * @param {{ vnTotal: number, redondeo?: number }} opciones
 * @returns {Array} [{ ordenId, vnAdjudicado }]
 */
export const prorratearAdjudicacion = (filas, { vnTotal, redondeo = 1 }) => {
  const paso = parseFloat(redondeo) > 0 ? parseFloat(redondeo) : 1;
  const total = parseFloat(vnTotal) || 0;
  const incluidas = filas.filter(f => f.incluida !== false);
  const solicitado = incluidas.reduce((acc, f) => acc + (parseFloat(f.cantidadVN) || 0), 0);

  if (total <= 0 || solicitado <= 0) {
    return filas.map(f => ({ ordenId: f.ordenId, vnAdjudicado: 0 }));
  }

  const factor = total / solicitado;
  const asignado = new Map();
  let restante = total;

  incluidas.forEach((fila, index) => {
    const pedido = parseFloat(fila.cantidadVN) || 0;
    const esUltima = index === incluidas.length - 1;
    const bruto = esUltima ? restante : Math.round(pedido * factor / paso) * paso;
    const vn = Math.max(0, Math.min(Math.round(bruto), Math.round(pedido), Math.round(restante)));
    asignado.set(fila.ordenId, vn);
    restante -= vn;
  });

  return filas.map(f => ({ ordenId: f.ordenId, vnAdjudicado: asignado.get(f.ordenId) || 0 }));
};

/**
 * Propuesta de adjudicación para una especie con su corte ya cargado.
 * No escribe nada: devuelve una fila editable por orden activa de la especie.
 */
export const proposeAdjudicacion = (ordenes, especie) => ordenes
  .filter(o => o.especieId === especie.id && isOrdenActiva(o))
  .map(o => {
    const adjudica = adjudicaOrden(o, especie);
    const vn = adjudica ? (parseFloat(o.cantidadVN) || 0) : 0;
    return {
      ordenId: o.id,
      estadoAdjudicacion: adjudica ? 'Adjudicada' : 'Rechazada',
      vnAdjudicado: vn,
      montoAdjudicado: adjudica ? montoPropuesto(vn, especie) : 0,
    };
  });

/**
 * Monto estimado a fondear por una orden ANTES de la adjudicación, en la moneda
 * de emisión de la especie (ver invariante de monedas arriba).
 * Modalidad Precio: VN x precio ofertado / 1000 (NC sin precio: VN).
 * Modalidad Tasa: VN (emisión a la par como aproximación).
 */
export const estimateMontoOrden = (orden, especie) => {
  const vn = parseFloat(orden.cantidadVN) || 0;
  if (especie?.modalidad === 'Precio') {
    const precio = parseFloat(orden.precioOfertado) || 0;
    return precio > 0 ? vn * precio / 1000 : vn;
  }
  return vn;
};

/**
 * Agrupa las órdenes activas de una especie por valor ofertado: el libro de
 * demanda tal como se lo mira antes de cargar en el mercado — cuánto VN se pidió
 * a cada tasa (o a cada precio) y cuántos clientes hay detrás de cada tramo.
 *
 * El Tramo No Competitivo no lleva tasa ni precio, así que agrupa aparte. El
 * resto se ordena por agresividad: en modalidad Tasa de menor a mayor tasa
 * ofertada, en modalidad Precio de mayor a menor precio. En ambos casos arriba
 * queda lo que adjudica primero; el No Competitivo va al final.
 *
 * `montoEstimado` sale de estimateMontoOrden y está en la MONEDA DE EMISIÓN de
 * la especie (ver invariante de monedas arriba): no se convierte a la moneda que
 * integra cada cliente porque un mismo tramo puede mezclar varias.
 *
 * `adjudicaAlCorte` es null mientras la especie no tenga corte cargado: antes de
 * la subasta no hay nada contra qué comparar.
 *
 * `ordenes` y `cantidadCargadas` permiten marcar el tramo entero como cargado en
 * el mercado de una sola vez: el libro se carga por tasa, no cliente por cliente.
 *
 * @returns {Array} [{ key, tramo, esNoCompetitivo, valor, vnSolicitado,
 *   vnAdjudicado, montoEstimado, cantidadOrdenes, cantidadClientes,
 *   cantidadCargadas, todasCargadas, pctDelTotal, adjudicaAlCorte, ordenes }]
 */
export const groupOrdenesPorTasa = (ordenes, especie) => {
  const esModalidadTasa = especie?.modalidad === 'Tasa';
  const tieneCorte = esModalidadTasa
    ? (parseFloat(especie?.tasaCorte) || 0) > 0
    : (parseFloat(especie?.precioCorte) || 0) > 0;
  const grupos = new Map();

  (ordenes || [])
    .filter(orden => isOrdenActiva(orden) && orden.especieId === especie?.id)
    .forEach(orden => {
      const esNoCompetitivo = orden.tramo === 'No Competitivo';
      const valor = esNoCompetitivo
        ? null
        : (parseFloat(esModalidadTasa ? orden.tasaOfertada : orden.precioOfertado) || 0);
      const key = esNoCompetitivo ? 'NC' : `C|${valor}`;

      if (!grupos.has(key)) {
        grupos.set(key, {
          key,
          tramo: esNoCompetitivo ? 'No Competitivo' : 'Competitivo',
          esNoCompetitivo,
          valor,
          vnSolicitado: 0,
          vnAdjudicado: 0,
          montoEstimado: 0,
          cantidadOrdenes: 0,
          // Todas las órdenes del grupo comparten tramo y valor ofertado, así que
          // adjudican o no adjudican en bloque.
          adjudicaAlCorte: tieneCorte ? adjudicaOrden(orden, especie) : null,
          cantidadCargadas: 0,
          comitentes: new Set(),
          ordenes: [],
        });
      }

      const grupo = grupos.get(key);
      grupo.vnSolicitado += parseFloat(orden.cantidadVN) || 0;
      grupo.vnAdjudicado += parseFloat(orden.vnAdjudicado) || 0;
      grupo.montoEstimado += estimateMontoOrden(orden, especie);
      grupo.cantidadOrdenes += 1;
      if (orden.cargado === true) grupo.cantidadCargadas += 1;
      grupo.comitentes.add(String(orden.nroComitente ?? ''));
      grupo.ordenes.push(orden);
    });

  const vnTotal = [...grupos.values()].reduce((acc, g) => acc + g.vnSolicitado, 0);

  return [...grupos.values()]
    .map(({ comitentes, ...grupo }) => ({
      ...grupo,
      cantidadClientes: comitentes.size,
      todasCargadas: grupo.cantidadCargadas === grupo.cantidadOrdenes,
      pctDelTotal: vnTotal > 0 ? grupo.vnSolicitado / vnTotal : 0,
    }))
    .sort((a, b) => {
      if (a.esNoCompetitivo !== b.esNoCompetitivo) return a.esNoCompetitivo ? 1 : -1;
      if (a.esNoCompetitivo) return 0;
      return esModalidadTasa ? a.valor - b.valor : b.valor - a.valor;
    });
};

// Normaliza la moneda de integración a los buckets de saldos (mismo criterio
// que monedaToCalc en useSaldosClientes).
export const monedaBucket = (moneda) =>
  (moneda === 'USD' || moneda === 'USD Mep' || moneda === 'USD CCL' || moneda === 'USD Cable')
    ? 'USD' : 'ARS';

export const getTipoCambio = (especie) => parseFloat(especie?.tipoCambio) || 0;

/**
 * Una especie emitida en USD que acepta integración en pesos (dólar linked o
 * dual) necesita tipo de cambio: se licita precio en USD pero el cliente
 * fondea pesos.
 */
export const requiereTipoCambio = (especie) =>
  especie?.monedaEmision === 'USD' && (especie?.monedasIntegracion || []).includes('Pesos');

/** La especie necesita TC y todavía no se cargó: los montos en ARS no sirven. */
export const faltaTipoCambio = (especie) =>
  requiereTipoCambio(especie) && getTipoCambio(especie) <= 0;

/**
 * Pasa un monto de la moneda de emisión de la especie a la moneda que integra el
 * cliente. Solo hay conversión USD -> ARS. Si falta el TC devuelve el monto sin
 * convertir y lo marca: es preferible que la UI avise "falta TC" antes que
 * mostrar como pesos un número que en realidad son dólares.
 *
 * @returns {{ monto: number, sinTipoCambio: boolean }}
 */
export const montoEnMonedaIntegracion = (monto, especie, monedaIntegracion) => {
  const convierte = especie?.monedaEmision === 'USD' && monedaBucket(monedaIntegracion) === 'ARS';
  if (!convierte) return { monto, sinTipoCambio: false };
  const tipoCambio = getTipoCambio(especie);
  if (tipoCambio <= 0) return { monto, sinTipoCambio: true };
  return { monto: monto * tipoCambio, sinTipoCambio: false };
};

/**
 * Monto a fondear de una orden para la vista de caja, ya expresado en la moneda
 * que el cliente integra: el adjudicado si la orden ya pasó por adjudicación, el
 * estimado si sigue pendiente. Las órdenes rechazadas no requieren caja.
 */
export const montoCajaOrden = (orden, especie) => {
  if (orden.estadoAdjudicacion === 'Rechazada') {
    return { monto: 0, estimado: false, sinTipoCambio: false };
  }
  const estimado = orden.estadoAdjudicacion !== 'Adjudicada';
  const enEmision = estimado
    ? estimateMontoOrden(orden, especie)
    : (parseFloat(orden.montoAdjudicado) || 0);
  const { monto, sinTipoCambio } = montoEnMonedaIntegracion(enEmision, especie, orden.monedaIntegracion);
  return { monto, estimado, sinTipoCambio };
};

/**
 * Agrupa las órdenes activas para el seguimiento de caja:
 * comercial -> cliente x moneda (bucket ARS/USD), con total a fondear,
 * flag de estimado y estado de caja consolidado del grupo.
 *
 * Devuelve: [{ comercial, grupos: [{ key, nroComitente, cliente, bucket,
 *   monto, esEstimado, sinTipoCambio, cajaHecha, cajaMarcadaPorNombre,
 *   cajaMarcadaAt, ordenes }] }]
 */
export const groupCajaPorComercialCliente = (ordenes, especiesById) => {
  const porComercial = new Map();

  ordenes.filter(isOrdenActiva).forEach(orden => {
    const especie = especiesById?.[orden.especieId];
    const { monto, estimado, sinTipoCambio } = montoCajaOrden(orden, especie);
    if (orden.estadoAdjudicacion === 'Rechazada') return;

    const comercial = orden.comercial || 'Sin comercial';
    const bucket = monedaBucket(orden.monedaIntegracion);
    const key = `${orden.nroComitente}|${bucket}`;

    if (!porComercial.has(comercial)) porComercial.set(comercial, new Map());
    const grupos = porComercial.get(comercial);

    if (!grupos.has(key)) {
      grupos.set(key, {
        key,
        comercial,
        nroComitente: orden.nroComitente,
        cliente: orden.cliente,
        bucket,
        monto: 0,
        esEstimado: false,
        sinTipoCambio: false,
        cajaHecha: true,
        cajaMarcadaPorNombre: '',
        cajaMarcadaAt: null,
        ordenes: [],
      });
    }
    const grupo = grupos.get(key);
    grupo.monto += monto;
    grupo.esEstimado = grupo.esEstimado || estimado;
    grupo.sinTipoCambio = grupo.sinTipoCambio || sinTipoCambio;
    // El grupo está "hecho" solo si TODAS sus órdenes tienen caja marcada.
    grupo.cajaHecha = grupo.cajaHecha && orden.cajaHecha === true;
    if (orden.cajaHecha && orden.cajaMarcadaPorNombre) {
      grupo.cajaMarcadaPorNombre = orden.cajaMarcadaPorNombre;
      grupo.cajaMarcadaAt = orden.cajaMarcadaAt || grupo.cajaMarcadaAt;
    }
    grupo.ordenes.push(orden);
  });

  return [...porComercial.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'es'))
    .map(([comercial, grupos]) => ({
      comercial,
      grupos: [...grupos.values()].sort((a, b) => a.cliente.localeCompare(b.cliente, 'es')),
    }));
};

/**
 * KPIs de una licitación a partir de sus órdenes activas.
 */
export const calculateKpisLicitacion = (ordenes, especiesById) => {
  const activas = ordenes.filter(isOrdenActiva);
  const kpis = {
    vnSolicitado: 0,
    vnAdjudicado: 0,
    montoARS: 0,
    montoUSD: 0,
    ordenesCargadas: 0,
    totalOrdenes: activas.length,
    // true si alguna orden depende de un TC que todavía no se cargó: el total
    // en ARS está incompleto y la UI tiene que avisarlo.
    sinTipoCambio: false,
  };
  activas.forEach(orden => {
    kpis.vnSolicitado += parseFloat(orden.cantidadVN) || 0;
    kpis.vnAdjudicado += parseFloat(orden.vnAdjudicado) || 0;
    if (orden.cargado) kpis.ordenesCargadas += 1;
    const especie = especiesById?.[orden.especieId];
    const { monto, sinTipoCambio } = montoCajaOrden(orden, especie);
    kpis.sinTipoCambio = kpis.sinTipoCambio || sinTipoCambio;
    if (monedaBucket(orden.monedaIntegracion) === 'ARS') kpis.montoARS += monto;
    else kpis.montoUSD += monto;
  });

  kpis.pctAdjudicado = kpis.vnSolicitado > 0 ? kpis.vnAdjudicado / kpis.vnSolicitado : 0;

  const gruposCaja = groupCajaPorComercialCliente(activas, especiesById)
    .flatMap(seccion => seccion.grupos);
  kpis.cajaTotal = gruposCaja.length;
  kpis.cajaHecha = gruposCaja.filter(g => g.cajaHecha).length;

  return kpis;
};

const BPS_POR_UNIDAD = 10000;

/** Fee de colocación de un monto: monto × bps / 10.000, redondeado a centavos. */
export const calcularFeeColocacion = (monto, feeBps) =>
  Math.round(((parseFloat(monto) || 0) * (parseFloat(feeBps) || 0) / BPS_POR_UNIDAD) * 100) / 100;

/**
 * TC para pasar a pesos el fee de una especie: el fee de colocación se cobra siempre en
 * pesos. 1 si la especie es en pesos; el TC cargado si es en USD (0 = falta).
 */
export const tipoCambioFee = (especie) => (especie?.monedaEmision === 'USD' ? getTipoCambio(especie) : 1);

/**
 * Fee ESPERADO de una licitación: el fee sobre el monto
 * adjudicado de cada orden activa y adjudicada, siempre EN PESOS (las especies en USD se
 * pasan con el TC de la especie; sin TC quedan pendientes). Es el mismo cálculo que
 * publica en el SGC la Cloud Function `functions/ingresosLicitaciones.js`
 * (docs/ingresos-licitaciones.md).
 *
 * @returns {{ ars: number, ordenesConIngreso: number, ordenesSinTipoCambio: number }}
 */
export const calculateIngresoColocacion = (ordenes, especiesById, feeBps) => {
  const ingreso = { ars: 0, ordenesConIngreso: 0, ordenesSinTipoCambio: 0 };
  if (!((parseFloat(feeBps) || 0) > 0)) return ingreso;

  (ordenes || []).filter(isOrdenActiva).forEach(orden => {
    if (orden.estadoAdjudicacion !== 'Adjudicada' || !((parseFloat(orden.montoAdjudicado) || 0) > 0)) return;
    const tipoCambio = tipoCambioFee(especiesById?.[orden.especieId]);
    if (!(tipoCambio > 0)) {
      ingreso.ordenesSinTipoCambio += 1;
      return;
    }
    const montoArs = Math.round((parseFloat(orden.montoAdjudicado) || 0) * tipoCambio * 100) / 100;
    ingreso.ars += calcularFeeColocacion(montoArs, feeBps);
    ingreso.ordenesConIngreso += 1;
  });
  ingreso.ars = Math.round(ingreso.ars * 100) / 100;
  return ingreso;
};

/**
 * Fee ESPERADO de una licitación con colocadores (docs/ingresos-licitaciones.md): cada
 * orden activa y adjudicada suma su monto en pesos × el bps de SU colocador / 10.000. La
 * orden sin colocador (licitación anterior a la que después se le cargaron varios) no
 * suma. Mismo cálculo que publica la Cloud Function `functions/ingresosLicitaciones.js`.
 *
 * @param {Array} ordenes
 * @param {object} especiesById
 * @param {Array<{ id, nombre, cuit }>} colocadores - los de la licitación
 * @param {object|null} feesPorColocador - { [colocadorId]: bps } de `licitacionesFees`
 * @returns {{ ars: number, porColocador: Array<{ id, nombre, cuit, feeBps: number|null,
 *   montoArs: number, ars: number }>, ordenesSinTipoCambio: number,
 *   ordenesSinColocador: number, feesCompletos: boolean, algunFeePositivo: boolean }}
 */
export const calculateFeePorColocador = (ordenes, especiesById, colocadores, feesPorColocador) => {
  const fees = feesPorColocador || {};
  const porColocador = (colocadores || []).map(c => ({
    id: c.id,
    nombre: c.nombre || '',
    cuit: c.cuit || '',
    feeBps: bpsONull(fees[c.id]),
    montoArs: 0,
    ars: 0,
  }));
  const filaPorId = new Map(porColocador.map(fila => [fila.id, fila]));
  const resultado = { ars: 0, ordenesSinTipoCambio: 0, ordenesSinColocador: 0 };

  (ordenes || []).filter(isOrdenActiva).forEach(orden => {
    const monto = parseFloat(orden.montoAdjudicado) || 0;
    if (orden.estadoAdjudicacion !== 'Adjudicada' || !(monto > 0)) return;
    const tipoCambio = tipoCambioFee(especiesById?.[orden.especieId]);
    if (!(tipoCambio > 0)) {
      resultado.ordenesSinTipoCambio += 1;
      return;
    }
    const colocador = resolverColocadorOrden(orden, colocadores);
    const fila = colocador ? filaPorId.get(colocador.id) : null;
    if (!fila) {
      resultado.ordenesSinColocador += 1;
      return;
    }
    const montoArs = Math.round(monto * tipoCambio * 100) / 100;
    fila.montoArs += montoArs;
    if (fila.feeBps > 0) fila.ars += calcularFeeColocacion(montoArs, fila.feeBps);
  });

  porColocador.forEach(fila => {
    fila.montoArs = Math.round(fila.montoArs * 100) / 100;
    fila.ars = Math.round(fila.ars * 100) / 100;
  });
  resultado.ars = Math.round(porColocador.reduce((acc, fila) => acc + fila.ars, 0) * 100) / 100;
  return {
    ...resultado,
    porColocador,
    feesCompletos: porColocador.length > 0 && porColocador.every(fila => fila.feeBps !== null),
    algunFeePositivo: porColocador.some(fila => fila.feeBps > 0),
  };
};

// Una licitación Liquidada ya está debitada por el custodio; una Eliminada no existe.
const ESTADOS_SIN_IMPACTO_EN_SALDO = ['Liquidada', 'Eliminada'];

/**
 * ¿La licitación descuenta hoy lo adjudicado del saldo en Control de Saldos?
 *
 * Ventana: desde la fecha de caja hasta el día anterior a la liquidación. El día
 * de liquidación el custodio debita los títulos y el snapshot ya lo trae: sumarlo
 * de nuevo lo duplicaría. Si caja y liquidación caen el mismo día, impacta ese día.
 *
 * @param {object} licitacion
 * @param {string} hoyIso - 'YYYY-MM-DD'
 */
export const licitacionImpactaSaldo = (licitacion, hoyIso) => {
  const fechaCaja = licitacion?.fechaCaja || '';
  if (!fechaCaja || !hoyIso || hoyIso < fechaCaja) return false;
  if (ESTADOS_SIN_IMPACTO_EN_SALDO.includes(licitacion.estado)) return false;
  if (hoyIso === fechaCaja) return true;
  return !!licitacion.fechaLiquidacion && hoyIso < licitacion.fechaLiquidacion;
};

const especieLabel = (especie) =>
  [especie?.denominacion, especie?.clase].filter(Boolean).join(' ');

/**
 * Débitos de las licitaciones adjudicadas para Control de Saldos: uno por
 * licitación x comitente x moneda, en la moneda que integra el cliente y con
 * signo negativo (el cliente paga los títulos).
 *
 * Entran las órdenes adjudicadas de las licitaciones en ventana de caja (ver
 * licitacionImpactaSaldo), aunque el resto de las especies siga sin adjudicar.
 * `cajaHecha` no interviene: marca que el cliente fondeó, pero la plata sigue en
 * la cuenta hasta que se liquida. Las órdenes que dependen de un TC sin cargar no
 * entran (serían dólares mostrados como pesos) y se cuentan aparte para avisarlo.
 *
 * La moneda sale de las tres que sigue Control de Saldos (constants/monedas): el
 * cable no se mezcla con el MEP, a diferencia de `monedaBucket`.
 *
 * @returns {{ movimientos: Array<{ id, licitacionId, nroComitente, cliente, moneda,
 *   monto, descripcion, cantidadOrdenes }>, ordenesSinTipoCambio: number }}
 */
export const buildDebitosLicitaciones = (licitaciones, ordenes, hoyIso) => {
  const enCaja = new Map((licitaciones || [])
    .filter(licitacion => licitacionImpactaSaldo(licitacion, hoyIso))
    .map(licitacion => [licitacion.id, licitacion]));
  const porClave = new Map();
  let ordenesSinTipoCambio = 0;

  (ordenes || []).forEach(orden => {
    const licitacion = enCaja.get(orden.licitacionId);
    if (!licitacion || !isOrdenActiva(orden) || orden.estadoAdjudicacion !== 'Adjudicada') return;
    const especie = (licitacion.especies || []).find(e => e.id === orden.especieId);
    const { monto, sinTipoCambio } = montoCajaOrden(orden, especie);
    if (sinTipoCambio) {
      ordenesSinTipoCambio += 1;
      return;
    }
    if (!(monto > 0)) return;

    const nroComitente = String(orden.nroComitente ?? '');
    const moneda = MONEDA_MOVIMIENTO[bucketDeMoneda(orden.monedaIntegracion)];
    const id = `${licitacion.id}|${nroComitente}|${moneda}`;
    if (!porClave.has(id)) {
      porClave.set(id, {
        id,
        licitacionId: licitacion.id,
        licitacionNombre: licitacion.nombre || '',
        nroComitente,
        cliente: orden.cliente || '',
        moneda,
        monto: 0,
        cantidadOrdenes: 0,
        especies: new Set(),
      });
    }
    const debito = porClave.get(id);
    debito.monto -= monto;
    debito.cantidadOrdenes += 1;
    if (especieLabel(especie)) debito.especies.add(especieLabel(especie));
  });

  const movimientos = [...porClave.values()].map(({ especies, licitacionNombre, ...debito }) => ({
    ...debito,
    descripcion: [`Licitación ${licitacionNombre}`.trim(), [...especies].join(', ')]
      .filter(Boolean).join(' — '),
  }));
  return { movimientos, ordenesSinTipoCambio };
};
